import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaUsers, FaUser, FaEye, FaCheck, FaDownload, FaSpinner, 
  FaPhone, FaEnvelope, FaClock, FaUserCheck 
} from 'react-icons/fa';

const AdvisorClientsSection = ({ 
  allClients, 
  pendingSubmissions,
  actionLoading, 
  handleGrantReportAccess, 
  handleGrantDownloadAccess 
}) => {
  const navigate = useNavigate();

  return (
    <>
      {/* Pending Client Submissions */}
      {pendingSubmissions.length > 0 && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-lg" data-testid="pending-submissions-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white flex items-center">
              <FaUserCheck className="mr-3 text-[#FFD700]" />
              Pending Client Submissions
              <span className="ml-3 px-3 py-1 bg-yellow-500/20 text-yellow-400 text-sm rounded-full">
                {pendingSubmissions.length} Awaiting Review
              </span>
            </h3>
          </div>
          
          <div className="space-y-4">
            {pendingSubmissions.map((client) => (
              <div 
                key={client.id} 
                className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F] hover:border-[#FFD700] transition-all"
                data-testid={`pending-client-${client.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <FaUserCheck className="text-yellow-400" />
                      <h4 className="text-white font-bold text-lg">{client.name || 'Client'}</h4>
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center">
                        <FaClock className="mr-1" /> Under Review
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-400">
                      {client.phone && <span className="flex items-center"><FaPhone className="mr-2 text-[#FFD700]" size={12} />{client.phone}</span>}
                      {client.email && <span className="flex items-center"><FaEnvelope className="mr-2 text-[#FFD700]" size={12} />{client.email}</span>}
                      {client.occupation && <span>Occupation: {client.occupation}</span>}
                      {client.submitted_at && <span>Submitted: {new Date(client.submitted_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => navigate(`/dashboard/clients/${client.id}/view`)}
                      className="px-4 py-2 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F] flex items-center space-x-2"
                      data-testid={`view-client-${client.id}`}
                    >
                      <FaEye /><span>View</span>
                    </button>
                    <button
                      onClick={() => handleGrantReportAccess(client.id, client.name)}
                      disabled={actionLoading[client.id] === 'report'}
                      className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold rounded-lg hover:from-green-500 hover:to-green-400 transition-all flex items-center space-x-2 disabled:opacity-50"
                      data-testid={`grant-access-${client.id}`}
                    >
                      {actionLoading[client.id] === 'report' ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                      <span>Grant Report Access</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Clients Section - Financial Planning */}
      {allClients.length > 0 && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-lg" data-testid="all-clients-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white flex items-center">
              <FaUsers className="mr-3 text-[#FFD700]" />
              My Clients - Financial Planning
              <span className="ml-3 px-3 py-1 bg-[#FFD700]/20 text-[#FFD700] text-sm rounded-full">
                {allClients.length} Total
              </span>
            </h3>
            <button
              onClick={() => navigate('/dashboard/clients')}
              className="px-4 py-2 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F]"
            >
              View All
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2D5A8F]">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Client</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Contact</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allClients.map((client) => {
                  const status = client.profile_status || 'draft';
                  const statusConfig = {
                    'draft': { label: 'Not Submitted', color: 'gray', bg: 'bg-gray-500/20' },
                    'submitted': { label: 'Pending Review', color: 'yellow', bg: 'bg-yellow-500/20' },
                    'report_access_granted': { label: 'Report Access', color: 'green', bg: 'bg-green-500/20' },
                    'download_allowed': { label: 'Download Ready', color: 'blue', bg: 'bg-blue-500/20' }
                  }[status] || { label: status, color: 'gray', bg: 'bg-gray-500/20' };

                  return (
                    <tr 
                      key={client.id} 
                      className="border-b border-[#2D5A8F]/50 hover:bg-[#0F172A]/50 transition-colors"
                      data-testid={`client-row-${client.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                            <FaUser className="text-[#FFD700]" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{client.name || 'Client'}</p>
                            <p className="text-gray-400 text-sm">{client.occupation || client.profile_occupation || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          {client.phone && <p className="text-gray-300">{client.phone}</p>}
                          {client.email && <p className="text-gray-400">{client.email}</p>}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig.bg} text-${statusConfig.color}-400`}>
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => navigate(`/dashboard/clients/${client.id}/view`)}
                            className="px-3 py-1.5 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F] flex items-center space-x-1 text-sm"
                            data-testid={`view-plan-${client.id}`}
                          >
                            <FaEye size={12} /><span>View/Plan</span>
                          </button>
                          
                          {status === 'submitted' && (
                            <button
                              onClick={() => handleGrantReportAccess(client.id, client.name)}
                              disabled={actionLoading[client.id] === 'report'}
                              className="px-3 py-1.5 bg-gradient-to-r from-green-600 to-green-500 text-white font-medium rounded-lg hover:from-green-500 hover:to-green-400 transition-all flex items-center space-x-1 text-sm disabled:opacity-50"
                              data-testid={`grant-report-${client.id}`}
                            >
                              {actionLoading[client.id] === 'report' ? <FaSpinner className="animate-spin" size={12} /> : <FaCheck size={12} />}
                              <span>Grant Report</span>
                            </button>
                          )}
                          
                          {status === 'report_access_granted' && (
                            <button
                              onClick={() => handleGrantDownloadAccess(client.id, client.name)}
                              disabled={actionLoading[client.id] === 'download'}
                              className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium rounded-lg hover:from-blue-500 hover:to-blue-400 transition-all flex items-center space-x-1 text-sm disabled:opacity-50"
                              data-testid={`grant-download-${client.id}`}
                            >
                              {actionLoading[client.id] === 'download' ? <FaSpinner className="animate-spin" size={12} /> : <FaDownload size={12} />}
                              <span>Grant Download</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AdvisorClientsSection;
